'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Button, HorizontalTabs, Spinner, Tooltip } from '~/components/primitives';
import Title from '~/components/utils/Title';
import { ActivityFiltersRail } from '~/features/activities/search/client/ActivityFilterDrawer';
import { useI18n } from '~/features/i18n';
import { Catalog_EditMode_RulesPanel } from './rules-panel';
import { useCatalog_EditMode_Session } from './session';
import { Catalog_EditMode_SaveButton } from './utils';
import type { ClassValue } from 'clsx';
import { cn } from '~/utils';

/** Browse-mode sidebar for the BIZZ booking page. Same chrome philosophy as
 * the WEB `<ActivityFiltersSidebar>` (sticky aside, rounded white card,
 * Filters title + total count) but BIZZ-owned so we don't drag WEB into
 * BIZZ-specific tweaks. Renders the portable `<ActivityFiltersRail />` for
 * the filter UI. */
export const Catalog_BrowseMode_Sidebar: React.FC<{
  totalResults: number | undefined;
  isFetching: boolean;
}> = props => {
  const $ = _c(11);
  const i18n = useI18n();
  let t0;
  if ($[0] !== i18n) {
    t0 = i18n._("Filters");
    $[0] = i18n;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <div className="flex items-center justify-between gap-3 px-6"><div className="flex min-w-0 flex-1 items-center gap-1.5"><Title size="lg" className="translate-y-px text-base leading-tight">{t0}</Title></div></div>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.isFetching || $[5] !== props.totalResults) {
    t2 = <SidebarResultsCount className="mb-2" isFetching={props.isFetching} totalResults={props.totalResults} />;
    $[4] = props.isFetching;
    $[5] = props.totalResults;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ActivityFiltersRail className="px-4 pb-4" />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2) {
    t4 = <aside data-testid="catalog-browse-sidebar" className="top-32 hidden overflow-hidden lg:sticky lg:block lg:self-start"><div className="flex h-full flex-col overflow-hidden rounded-[28px] bg-white/0">{t1}{t2}{t3}</div></aside>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};

/** Edit-mode sidebar: bordered aside chrome + wizard step strip
 * (`Filter rules | Finetune selection`) + body switch + per-step footer.
 *
 * Layout: capped max-height + sticky. Tabs and result count are pinned at
 * the top, the active step's body scrolls in the middle, the footer is
 * pinned at the bottom of the aside. All horizontal padding around pane
 * content is owned by this sidebar so both steps look identical. */
export const Catalog_EditMode_Sidebar: React.FC<{
  totalResults: number | undefined;
  isFetching: boolean;
}> = props => {
  const $ = _c(32);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  const step = session.step ?? "rules";
  const asideRef = useRef(null);
  const maxHeight = useStickyMaxHeight(asideRef);
  let t0;
  if ($[0] !== maxHeight) {
    t0 = {
      maxHeight
    };
    $[0] = maxHeight;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = session.setStep;
  let t2;
  if ($[2] !== i18n) {
    t2 = i18n._("bizz.booking.edit.wizard.step1", undefined, {
      message: "Filter rules"
    });
    $[2] = i18n;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `1. ${t2}`;
  let t4;
  if ($[4] !== t3) {
    t4 = {
      value: "rules" as const,
      label: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== i18n) {
    t5 = i18n._("bizz.booking.edit.wizard.step2", undefined, {
      message: "Finetune selection"
    });
    $[6] = i18n;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = `2. ${t5}`;
  let t7;
  if ($[8] !== t6) {
    t7 = {
      value: "selection" as const,
      label: t6
    };
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t7) {
    t8 = [t4, t7];
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== i18n) {
    t9 = t10 => {
      const {
        tab
      } = t10;
      return <Tooltip content={{
        rules: i18n._("bizz.booking.edit.wizard.step1.tooltip", undefined, {
          message: "Set global rules (regions, categories, budget) that decide which activities belong in the catalog by default."
        }),
        selection: i18n._("bizz.booking.edit.wizard.step2.tooltip", undefined, {
          message: "Manually add or remove individual activities to fine-tune the catalog beyond the rules."
        })
      }[tab.value]} position="bottom" delay={200} className="max-w-xs"><span>{tab.label}</span></Tooltip>;
    };
    $[13] = i18n;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== session.setStep || $[16] !== step || $[17] !== t8 || $[18] !== t9) {
    t10 = <div className="shrink-0 border-b border-foreground/10 p-4"><HorizontalTabs.StateLess size="small" value={step} onChange={t1} className="w-full" layout="fill" tabs={t8} renderTab={t9} /></div>;
    $[15] = session.setStep;
    $[16] = step;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== props.isFetching || $[21] !== props.totalResults) {
    t11 = <SidebarResultsCount className="my-2" isFetching={props.isFetching} totalResults={props.totalResults} />;
    $[20] = props.isFetching;
    $[21] = props.totalResults;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  let t13;
  if ($[23] !== step) {
    t12 = step === "rules" ? <RulesPaneBody /> : <ActivityFiltersRail className="px-4 pb-4" showCopyLink={false} />;
    t13 = step === "rules" ? <RulesStepFooter /> : <SelectionStepFooter />;
    $[23] = step;
    $[24] = t12;
    $[25] = t13;
  } else {
    t12 = $[24];
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t0 || $[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13) {
    t14 = <aside ref={asideRef} data-testid="catalog-edit-sidebar" style={t0} className="top-24 hidden flex-col overflow-hidden rounded-3xl bg-white shadow-sm lg:sticky lg:flex lg:self-start">{t10}{t11}{t12}{t13}</aside>;
    $[26] = t0;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};

/** `top-24` — the sticky offset of both BIZZ sidebars, in px. */
const SIDEBAR_STICKY_TOP_PX = 96;
/** Breathing room between the bottom of the sidebar and the viewport edge. */
const SIDEBAR_BOTTOM_GUTTER_PX = 16;

/** Live `max-height` for a sticky sidebar, derived from its current position in
 * the viewport. Unscrolled, the aside starts below the page header + the yellow
 * edit banner, so a fixed `100vh - 7rem` cap either overflows the viewport or
 * wastes space once those scroll away. Reading the element's own viewport top
 * per scroll frame keeps the sidebar exactly as tall as the space it actually
 * has, growing as the page scrolls until it is stuck at `top-24`.
 *
 * `useSyncExternalStore` keeps the layout read out of render and re-renders only
 * when the rounded px value actually changes. */
function useStickyMaxHeight(ref) {
  const $ = _c(2);
  let t0;
  if ($[0] !== ref) {
    t0 = () => Math.max(Math.round(ref.current?.getBoundingClientRect().top ?? SIDEBAR_STICKY_TOP_PX), SIDEBAR_STICKY_TOP_PX);
    $[0] = ref;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const top = useSyncExternalStore(_temp, t0, _temp2);
  return `max(60vh, calc(100vh - ${top + SIDEBAR_BOTTOM_GUTTER_PX}px))`;
}

/** Animated `{totalResults} teambuildings` row beneath the sidebar header.
 * Lives here so both BIZZ sidebars stay visually aligned with the WEB one. */
function _temp2() {
  return SIDEBAR_STICKY_TOP_PX;
}
function _temp(onStoreChange) {
  window.addEventListener("scroll", onStoreChange, {
    passive: true
  });
  window.addEventListener("resize", onStoreChange);
  return () => {
    window.removeEventListener("scroll", onStoreChange);
    window.removeEventListener("resize", onStoreChange);
  };
}
const SidebarResultsCount: React.FC<{
  className?: ClassValue;
  isFetching: boolean;
  totalResults: number | undefined;
}> = props => {
  const $ = _c(9);
  const i18n = useI18n();
  if (!props.isFetching && props.totalResults === undefined) {
    return null;
  }
  let t0;
  if ($[0] !== props.className) {
    t0 = cn("flex min-h-8 w-full min-w-0 shrink-0 items-center px-6", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== i18n || $[3] !== props.isFetching || $[4] !== props.totalResults) {
    t1 = <AnimatePresence mode="popLayout" initial={false} presenceAffectsLayout={false}>{props.isFetching ? <motion.span key="sidebar-total-loading" layout={true} initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.94
      }} transition={{
        type: "spring",
        stiffness: 420,
        damping: 32
      }} className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap text-sm font-medium text-blue-400"><Spinner className="size-3.5! shrink-0" aria-hidden={true} /><span className="min-w-0 truncate">{i18n._("activity.overview.filter.loading_teambuildings", undefined, {
            message: "Loading teambuildings.."
          })}</span></motion.span> : <motion.span key={`sidebar-total-${props.totalResults}`} layout={true} data-testid="sidebar-total-results" initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.94
      }} transition={{
        type: "spring",
        stiffness: 420,
        damping: 32
      }} className="block max-w-full whitespace-nowrap text-sm font-medium text-blue-400">{props.totalResults} {i18n._("teambuildings")}</motion.span>}</AnimatePresence>;
    $[2] = i18n;
    $[3] = props.isFetching;
    $[4] = props.totalResults;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = <div className={t0}>{t1}</div>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};

/** Scrollable body of the Rules pane. Hides the native scrollbar and shows
 * soft top/bottom fades only when there is content past that edge — at rest
 * (scrollTop = 0, fully visible) no fade overlays the title or the last
 * rule. Tracks edge state via a single scroll/resize observer on the
 * inner scroll element. */
const RulesPaneBody: React.FC = () => {
  const $ = _c(15);
  const scrollRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      top: false,
      bottom: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [edges, setEdges] = useState(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const update = () => {
        setEdges({
          top: el.scrollTop > 4,
          bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 4
        });
      };
      update();
      el.addEventListener("scroll", update, {
        passive: true
      });
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => {
        el.removeEventListener("scroll", update);
        ro.disconnect();
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div ref={scrollRef} className="hide-scrollbar min-h-0 flex-1 overflow-y-auto px-6 pb-4"><Catalog_EditMode_RulesPanel /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = edges.top ? "opacity-100" : "opacity-0";
  let t5;
  if ($[4] !== t4) {
    t5 = cx("bg-linear-to-b pointer-events-none absolute inset-x-0 top-0 h-3 from-white to-transparent transition-opacity", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <div aria-hidden={true} className={t5} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = edges.bottom ? "opacity-100" : "opacity-0";
  let t8;
  if ($[8] !== t7) {
    t8 = cx("bg-linear-to-t pointer-events-none absolute inset-x-0 bottom-0 h-3 from-white to-transparent transition-opacity", t7);
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <div aria-hidden={true} className={t8} />;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t6 || $[13] !== t9) {
    t10 = <div className="relative flex min-h-0 flex-1 flex-col">{t3}{t6}{t9}</div>;
    $[12] = t6;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
};

/** Step-1 (Filter rules) footer. Left button: "Discard changes" (revert to
 * the committed rules) when dirty, "Cancel" (exit edit mode) when clean.
 * The right button publishes the rules *and* advances the wizard, so an admin
 * who leaves after step 1 doesn't silently lose their rule edits. On a failed
 * save it stays on step 1 (`save` already surfaced the error toast) so the
 * changes can be retried instead of being carried into step 2 unsaved. */
const RulesStepFooter: React.FC = () => {
  const $ = _c(20);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  let t0;
  if ($[0] !== session) {
    t0 = async () => {
      if (session.hasChanges) {
        try {
          await session.save();
        } catch {
          return;
        }
      }
      session.setStep("selection");
    };
    $[0] = session;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const saveAndAdvance = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx("flex shrink-0 items-center justify-between gap-2", "border-t border-foreground/10 bg-white px-4 py-3");
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = session.hasChanges ? session.reset : session.exit;
  const t3 = session.hasChanges ? "trash" : "cross";
  let t4;
  if ($[3] !== i18n || $[4] !== session.hasChanges) {
    t4 = session.hasChanges ? i18n._("bizz.booking.edit.rules.reset", undefined, {
      message: "Discard changes"
    }) : i18n._("bizz.booking.edit.button.cancel", undefined, {
      message: "Cancel"
    });
    $[3] = i18n;
    $[4] = session.hasChanges;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== session.isSaving || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <Button variant="ghost" size="sm" onClick={t2} disabled={session.isSaving} iconBefore={t3} data-testid="bizz-booking-rules-cancel">{t4}</Button>;
    $[6] = session.isSaving;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = session.isSaving;
  const t7 = session.isSaving;
  let t8;
  if ($[11] !== i18n) {
    t8 = i18n._("bizz.booking.edit.wizard.saveAndNextStep", undefined, {
      message: "Save & finetune selection"
    });
    $[11] = i18n;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== saveAndAdvance || $[14] !== session.isSaving || $[15] !== t8) {
    t9 = <Button variant="tertiary" size="sm" iconAfter="chevron-right" onClick={saveAndAdvance} disabled={t6} isLoading={t7} data-testid="bizz-booking-rules-next">{t8}</Button>;
    $[13] = saveAndAdvance;
    $[14] = session.isSaving;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t9) {
    t10 = <footer data-testid="bizz-booking-rules-footer" className={t1}>{t5}{t9}</footer>;
    $[17] = t5;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};

/** Step-2 (Finetune selection) footer. Left button: "Discard changes" (revert)
 * when dirty, "Cancel" (exit edit mode) when clean. Save & publish persists
 * then leaves edit mode (`session.saveAndExit`) — also surfaced on the sticky
 * view-tab bar so it stays reachable while scrolling the grid. */
const SelectionStepFooter: React.FC = () => {
  const $ = _c(12);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cx("flex shrink-0 items-center justify-between gap-2", "border-t border-foreground/10 bg-white px-4 py-3");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = session.hasChanges ? session.reset : session.exit;
  const t2 = session.hasChanges ? "trash" : "cross";
  let t3;
  if ($[1] !== i18n || $[2] !== session.hasChanges) {
    t3 = session.hasChanges ? i18n._("bizz.booking.edit.rules.reset", undefined, {
      message: "Discard changes"
    }) : i18n._("bizz.booking.edit.button.cancel", undefined, {
      message: "Cancel"
    });
    $[1] = i18n;
    $[2] = session.hasChanges;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== session.isSaving || $[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = <Button variant="ghost" size="sm" onClick={t1} disabled={session.isSaving} iconBefore={t2} data-testid="bizz-booking-selection-cancel">{t3}</Button>;
    $[4] = session.isSaving;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Catalog_EditMode_SaveButton testId="bizz-booking-selection-save" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = <footer data-testid="bizz-booking-selection-footer" className={t0}>{t4}{t5}</footer>;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};